import { Component, OnInit, inject } from '@angular/core';
import { BehaviorSubject, Subject, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Pagination } from '@peca/design-system';
import { AsyncState } from '@peca/backoffice/models/async-state.model';
import { Pageable } from '@peca/backoffice/models/pageable.model';

import { SettingsService } from '../../../services/settings.service';
import { TSetting } from '../../../types/settings.type';
import { GenericLog, GenericLogsQuery } from '../../../../generic-logs/models/generic-log.model';
import { GenericLogSettings } from '../../../../generic-logs/models/generic-log-settings';
import { convertPageToLogs } from '../../../../generic-logs/utils/page-to-log.converter';

type OptimizerSettings = {
    fixedCostPerProduct?: number | null;
    maxDiffCostPerProduct?: number | null;
    maxDiffCostPerProductPercentage?: number | null;
};

type SettingVersion = TSetting<OptimizerSettings>;

@Component({
    selector: 'peca-optimizer-logs',
    templateUrl: './optimizer-logs.component.html',
    styleUrls: ['./optimizer-logs.component.scss'],
    standalone: false,
})
export class OptimizerLogsComponent implements OnInit {
    private readonly SETTING_KEY = 'optimizer';
    private readonly settingsService = inject(SettingsService);

    logs$ = new BehaviorSubject<AsyncState<GenericLog>>({ loading: true });
    settings$ = new BehaviorSubject<AsyncState<GenericLogSettings>>({
        loading: false,
        data: { pipeOfField: {}, includeIds: false },
    });
    pagination$ = new Subject<Pagination>();

    private lastQuery: GenericLogsQuery = {
        page: 1,
        size: 12,
        sort: 'version,DESC',
    };

    ngOnInit(): void {
        this.fetchLogs();
    }

    fetchLogs(): void {
        this.logs$.next({ loading: true });

        const currentPage$ = this.settingsService.getAllVersionsOfSetting<SettingVersion>(
            this.SETTING_KEY,
            {
                page: this.lastQuery.page,
                size: this.lastQuery.size,
                sort: this.lastQuery.sort,
            }
        );

        // Busca o 1° item da próxima página para servir como `from` do último
        // item da página atual (que não tem predecessor dentro da página).
        // Como usamos size=1, calculamos a posição absoluta correta:
        // nextAbsolutePage = currentPage * pageSize + 1
        // Ex: página 1, size 12 → próximo item está na posição 13 → page=13 com size=1.
        const currentPage = this.lastQuery.page ?? 1;
        const currentSize = this.lastQuery.size ?? 12;
        const nextAbsolutePage = currentPage * currentSize + 1;

        const nextPageFirstItem$ = this.settingsService
            .getAllVersionsOfSetting<SettingVersion>(this.SETTING_KEY, {
                page: nextAbsolutePage,
                size: 1,
                sort: this.lastQuery.sort,
            })
            .pipe(
                map((p) => p.items[0] ?? null),
                catchError(() => of(null))
            );

        forkJoin({ current: currentPage$, nextFirst: nextPageFirstItem$ }).subscribe({
            next: ({ current, nextFirst }) => {
                const logPage = convertPageToLogs(current, nextFirst);
                this.logs$.next({ data: logPage, loading: false });
                this.updatePagination(logPage);
            },
            error: () => {
                this.logs$.next({ loading: false });
            },
        });
    }

    onPaginate(pagination: Pagination): void {
        this.lastQuery = {
            ...this.lastQuery,
            page: pagination.page,
            size: pagination.size,
        };
        this.fetchLogs();
    }

    private updatePagination(page: Pageable<unknown>): void {
        this.lastQuery = {
            ...this.lastQuery,
            page: page.currentPage,
            size: page.pageSize,
        };

        this.pagination$.next({
            page: page.currentPage,
            size: page.pageSize,
            total: page.totalPages,
            items: page.totalItems,
            disabled: page.totalPages <= 1,
        });
    }
}
